import { useEffect, type RefObject } from 'react';

/* ==========================================================================
   Twee animaties voor de landingspagina, allebei met anime.js.

   Alleen daar: binnen de app zit je te werken en dan is beweging in de weg.
   Op het scherm dat je nog moet overtuigen mag het.

   anime.js wordt met een dynamische import gehaald, en dus pas gedownload als
   deze haken werkelijk draaien. Dat scheelt iedereen die is ingelogd zo'n
   twintig kilobyte die hij toch nooit ziet.

   Drie dingen die niet onderhandelbaar zijn:

   - Wie `prefers-reduced-motion` aan heeft ziet de tekst gewoon staan. Niet
     een snellere animatie, maar géén.
   - De tekst staat er ook zonder JavaScript, en ook als de import faalt. We
     animeren wat er al is; we bouwen het niet op.
   - In een tabblad op de achtergrond staat requestAnimationFrame stil. Daar
     beginnen we dus niet aan, anders staat de belangrijkste zin van de pagina
     op onzichtbaar te wachten tot iemand terugklikt.
   ========================================================================== */

function magBewegen(): boolean {
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Doet het werk zodra het tabblad in beeld is; anders wacht hij netjes af. */
function zodraZichtbaar(werk: () => void): () => void {
  if (!document.hidden) {
    werk();
    return () => undefined;
  }

  const bijTerugkeer = () => {
    if (document.hidden) return;
    document.removeEventListener('visibilitychange', bijTerugkeer);
    werk();
  };

  document.addEventListener('visibilitychange', bijTerugkeer);
  return () => document.removeEventListener('visibilitychange', bijTerugkeer);
}

/**
 * De kop valt woord voor woord binnen. `splitText` knipt de zin in losse
 * stukjes en zet ze in spans; anime.js laat ze daarna oplopend omhoog komen.
 */
export function useOpening(doel: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = doel.current;
    if (!el || !magBewegen()) return;

    let opruimen: (() => void) | null = null;
    let afgebroken = false;

    const stop = zodraZichtbaar(async () => {
      const { animate, splitText, stagger } = await import('animejs');
      if (afgebroken) return;

      /* `accessible` zet een verborgen kopie van de hele zin neer, zodat een
         schermlezer één zin hoort en geen losse woorden. */
      const stukken = splitText(el, { words: { wrap: 'clip' }, chars: false, accessible: true });

      const beweging = animate(stukken.words, {
        y: [{ from: '100%', to: '0%' }],
        opacity: [{ from: 0, to: 1 }],
        duration: 700,
        delay: stagger(60),
        ease: 'out(3)'
      });

      /* De woorden beginnen op opacity 0, en dat is de belangrijkste zin van de
         pagina. Loopt de animatie om wat voor reden dan ook niet af, dan zetten
         we hem alsnog neer: beter zonder animatie dan onzichtbaar. */
      const vangnet = window.setTimeout(() => {
        for (const woord of stukken.words) {
          (woord as HTMLElement).style.opacity = '1';
          (woord as HTMLElement).style.transform = 'none';
        }
      }, 2500);

      opruimen = () => {
        window.clearTimeout(vangnet);
        beweging.revert();
        stukken.revert();
      };
    });

    return () => {
      afgebroken = true;
      stop();
      opruimen?.();
    };
  }, [doel]);
}

/**
 * De kop van de tweede sectie rammelt zichzelf in elkaar zodra hij in beeld
 * komt — en maar één keer: bij elke scroll opnieuw is het een tic, geen effect.
 */
export function useScrambleBijScroll(doel: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const el = doel.current;
    if (!el || !magBewegen()) return;

    let kijker: IntersectionObserver | null = null;
    let afgebroken = false;

    void (async () => {
      const { animate, scrambleText } = await import('animejs');
      if (afgebroken) return;

      kijker = new IntersectionObserver(
        (regels) => {
          for (const regel of regels) {
            if (!regel.isIntersecting) continue;
            kijker?.disconnect();
            animate(el, {
              text: scrambleText({ chars: 'A-Z', from: 'left' }),
              duration: 1600,
              ease: 'out(2)'
            });
          }
        },
        { threshold: 0.6 }
      );

      kijker.observe(el);
    })();

    return () => {
      afgebroken = true;
      kijker?.disconnect();
    };
  }, [doel]);
}
